import { call, put, takeLatest } from 'redux-saga/effects';
import type { CallEffect, PutEffect } from 'redux-saga/effects';

import api from 'src/services/graphicsApi';

import { NOTIFY } from '../reducers/notification';
import { actions } from '../reducers/canvas';

type CanvasApiResponse = void | { data: Canvas; status: number };
function* createCanvas(
  action: ReturnType<typeof actions.requestCreateCanvas>
): Generator<CallEffect | PutEffect, void, CanvasApiResponse> {
  try {
    const { navigate, data } = action.payload;
    const response = yield call(api.post, `/canvases`, data);

    if (response?.data) {
      yield put(actions.receiveCreateCanvas());
      navigate && navigate('/settings/workspace/canvases');
      yield put({
        type: NOTIFY,
        payload: {
          variant: 'success',
          title: 'Canvas created',
        },
      });
    }
  } catch (error) {
    const apiError = error as Error;
    yield put({
      type: NOTIFY,
      payload: {
        variant: 'error',
        title: apiError.name,
        description: apiError.message,
      },
    });
    yield put(
      actions.failedCreateCanvas(((error as Error)?.message as string) || 'Something went wrong')
    );
  }
}

function* deleteCanvas(
  action: ReturnType<typeof actions.requestDeleteCanvas>
): Generator<CallEffect | PutEffect, void> {
  try {
    const { id } = action.payload;
    const response = yield call(api.delete, `/canvases/${id}`);
    const apiResponse = response as CanvasApiResponse;
    if (apiResponse?.status === 200 || apiResponse?.status === 204) {
      yield put(actions.receiveDeleteCanvas(id));

      yield put({
        type: NOTIFY,
        payload: {
          variant: 'info',
          title: 'Canvas deleted',
        },
      });
    }
  } catch (error) {
    const apiError = error as Error;
    yield put({
      type: NOTIFY,
      payload: {
        variant: 'error',
        title: apiError.name,
        description: apiError.message,
      },
    });
    yield put(
      actions.failedDeleteCanvas(((error as Error)?.message as string) || 'Something went wrong')
    );
  }
}

type CanvasesApiResponse = void | { data: Canvas[]; status: number };
function* getCanvases(): Generator<
  PutEffect | CallEffect<CanvasesApiResponse>,
  void,
  CanvasesApiResponse
> {
  try {
    const response = yield call(api.get, `/canvases`);
    const apiResponse = response as CanvasesApiResponse;

    if (apiResponse?.data) {
      yield put(actions.receiveGetCanvases(apiResponse.data));
    }
  } catch (error) {
    yield put(
      actions.failedGetCanvases(((error as Error)?.message as string) || 'Something went wrong')
    );
  }
}

function* updateCanvas(
  action: ReturnType<typeof actions.requestUpdateCanvas>
): Generator<CallEffect | PutEffect, void, CanvasApiResponse> {
  try {
    const { data, id, navigate } = action.payload;
    const response = yield call(api.patch, `/canvases/${id}`, data);

    if (response?.data) {
      yield put(actions.receiveUpdateCanvas());
      yield put(actions.requestGetCanvases());

      navigate && navigate('/settings/workspace/canvases');
      yield put({
        type: NOTIFY,
        payload: {
          variant: 'success',
          title: 'Canvas updated',
        },
      });
    }
  } catch (error) {
    const apiError = error as Error;
    yield put({
      type: NOTIFY,
      payload: {
        variant: 'error',
        title: apiError.name,
        description: apiError.message,
      },
    });
    yield put(
      actions.failedUpdateCanvas(((error as Error)?.message as string) || 'Something went wrong')
    );
  }
}

type CanvasBackgroundsApiResponse = void | { data: CanvasBackground[]; status: number };
function* getCanvasBackgrounds(): Generator<
  PutEffect | CallEffect<CanvasBackgroundsApiResponse>,
  void,
  CanvasBackgroundsApiResponse
> {
  try {
    const response = yield call(api.get, `/canvasbackgrounds`);
    if (response?.data) {
      yield put(actions.receiveGetCanvasBackgrounds(response.data));
    } else {
      yield put(actions.failedGetCanvasBackgrounds('No data found'));
    }
  } catch (error) {
    yield put(
      actions.failedGetCanvasBackgrounds(
        ((error as Error)?.message as string) || 'Something went wrong'
      )
    );
  }
}

export default function* root() {
  yield takeLatest(actions.requestCreateCanvas.type, createCanvas);
  yield takeLatest(actions.requestDeleteCanvas.type, deleteCanvas);
  yield takeLatest(actions.requestGetCanvases.type, getCanvases);
  yield takeLatest(actions.requestUpdateCanvas.type, updateCanvas);
  yield takeLatest(actions.requestGetCanvasBackgrounds.type, getCanvasBackgrounds);
}
